/**
 * DocumentTabArea — the bridge between yap's single-active-document workbench
 * and the suite's shared document workspace (`@suite/documents`).
 *
 * The workbench content (the routed mode element) renders as the ACTIVE tab's
 * surface; background documents render a paused card and reopen through the
 * existing `openRecentDocument` pipeline when activated. The close request
 * runs yap's own unsaved guard.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { WorkspaceShell } from '@suite/documents';
import { documentWorkspace } from './documentWorkspace';
import { useDocSession } from './sessionStore';

export interface DocumentTabAreaProps {
  /** Key of the live document (`kind:displayName` from the document store) */
  activeKey: string;
  /** The routed workbench element (rendered as the active surface) */
  activeSurface: ReactNode;
  /** When provided, every group's tab strip portals into this chrome
      element (Layout's slot under the suite top bar) instead of rendering
      inside the group pane — one identical, always-visible strip placement
      across every view mode. */
  stripPortalTarget?: HTMLElement | null;
  /** Reopen a background document through the app's existing pipeline */
  onActivate: (key: string) => void;
  /** Close request — App owns the unsaved guard and navigation */
  onCloseRequest: (key: string) => void;
}

export const DocumentTabArea = ({
  activeKey,
  activeSurface,
  stripPortalTarget,
  onActivate,
  onCloseRequest,
}: DocumentTabAreaProps) => {
  const docs = useDocSession((state) => state.docs);
  const sessionActiveKey = useDocSession((state) => state.activeKey);
  // Last session active key the mirror has seen. The active tab is only
  // mirrored when it CHANGES: park() mutates `docs` mid-activation while the
  // shell has already activated the clicked tab, and re-asserting the stale
  // session key here would revert the user's in-flight tab switch.
  const mirroredActiveKeyRef = useRef<string | null | undefined>(undefined);

  // One-directional mirror: session → shared store (open missing tabs, drop
  // closed ones, follow the dirty flag and the active tab).
  useEffect(() => {
    const ws = documentWorkspace.store.getState();
    Object.values(docs).forEach((doc) => {
      if (!ws.tabs[doc.key]) {
        ws.openTab({
          id: doc.key,
          kind: doc.kind,
          label: doc.name,
          isDirty: doc.dirty,
        });
      } else if (ws.tabs[doc.key].isDirty !== doc.dirty) {
        ws.setDirty(doc.key, doc.dirty);
      } else if (ws.tabs[doc.key].label !== doc.name) {
        ws.updateTab(doc.key, { label: doc.name });
      }
    });
    ws.tabIds.forEach((id) => {
      if (!docs[id]) ws.closeTab(id);
    });
    if (
      sessionActiveKey &&
      mirroredActiveKeyRef.current !== undefined &&
      sessionActiveKey !== mirroredActiveKeyRef.current &&
      ws.activeTabId !== sessionActiveKey
    ) {
      ws.activateTab(sessionActiveKey);
    }
    mirroredActiveKeyRef.current = sessionActiveKey;
  }, [docs, sessionActiveKey]);

  return (
    <WorkspaceShell
      workspace={documentWorkspace}
      stripPortalTarget={stripPortalTarget}
      renderSurface={(ctx) =>
        ctx.tab.id === activeKey ? (
          activeSurface
        ) : (
          <div className="yap-doc-paused" data-suite-selectable>
            <p className="yap-doc-paused__title">{ctx.tab.label}</p>
            <p className="yap-doc-paused__hint">
              Background document — activate the tab to reopen it through the
              workspace pipeline.
            </p>
          </div>
        )
      }
      emptyMessage="No document open."
      noDocumentsLabel="No documents"
      onActivate={onActivate}
      onCloseRequest={onCloseRequest}
    />
  );
};
